import type { FC } from 'react';
import { Divider, Flex, Text } from '@chakra-ui/react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import {
    getGiftCardData,
    getSDDShippingTotal,
    getShipToShippingTotal,
    getShippingDiscount,
} from '../../bff-api/utils/basket-utils';
import { useCart } from '../../hooks/useCart';
import { useSameDayDelivery } from '../../hooks/useSameDayDelivery';
import { BasketOrderTotalSummary } from '../basket-order-total-summary';

export const BasketSummary: FC<{ isFromCart?: boolean }> = ({ isFromCart = false }) => {
    const { isNextDayDelivery } = useSameDayDelivery({ preferenceForBasketValue: true });
    const {
        hasNonSameDayShippingProducts,
        countTotalProductItems,
        currency,
        basket = {},
    } = useCart();
    const {
        c_productSubTotalIncludingAdjustments = 0,
        taxTotal = 0,
        c_handlingFee = 0,
        c_promoSavings = 0,
    } = basket;
    const giftCards = getGiftCardData(basket);
    const shipToShippingTotal = getShipToShippingTotal(basket);
    const shippingDiscount = getShippingDiscount(basket);
    const sddShippingTotal = getSDDShippingTotal(basket);
    const promoSaving = c_promoSavings || shippingDiscount;

    let promoCodeSavingsColor = 'slt-black-900';
    if (isFromCart) {
        promoCodeSavingsColor = 'slt-pepper.1000';
    }
    if (promoSaving > 0 && !isFromCart) {
        promoCodeSavingsColor = 'slt-success-green.1000';
    }

    const textStyle = isFromCart ? 'body-base-pepper-1000' : 'slt-body-text-lg';

    let shippingElement: string | JSX.Element = '-';
    shippingElement =
        shipToShippingTotal > 0 ? (
            <FormattedNumber style="currency" currency={currency} value={shipToShippingTotal} />
        ) : (
            <FormattedMessage id="order_summary.label_free" defaultMessage="Free" />
        );

    return (
        <>
            <Flex justifyContent="space-between" alignItems="center" marginTop={6}>
                <Text data-testid="order-summary-subtotal-label" textStyle={textStyle}>
                    <FormattedMessage
                        id="order_summary.subtotal"
                        defaultMessage="Subtotal ({count})"
                        values={{ count: countTotalProductItems }}
                    />
                </Text>

                <Text data-testid="order-summary-subtotal-value" textStyle={textStyle}>
                    <FormattedNumber
                        style="currency"
                        currency={currency}
                        value={c_productSubTotalIncludingAdjustments}
                    />
                </Text>
            </Flex>

            {promoSaving ? (
                <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                    <Text
                        data-testid="order-summary-promo-code-savings-label"
                        textStyle={textStyle}
                        color={promoCodeSavingsColor}
                    >
                        <FormattedMessage
                            id="order_summary.promo_code_saving"
                            defaultMessage="Promo Code Savings"
                        />
                    </Text>

                    <Text
                        data-testid="order-summary-promo-code-savings-value"
                        textStyle={textStyle}
                        color={promoCodeSavingsColor}
                    >
                        <>
                            -
                            <FormattedNumber
                                style="currency"
                                currency={currency}
                                value={promoSaving}
                            />
                        </>
                    </Text>
                </Flex>
            ) : null}

            {hasNonSameDayShippingProducts ? (
                <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                    <Text data-testid="order-summary-shipping-label" textStyle={textStyle}>
                        <FormattedMessage id="order_summary.shipping" defaultMessage="Shipping" />
                    </Text>

                    <Text data-testid="order-summary-shipping-value" textStyle={textStyle}>
                        {shippingElement}
                    </Text>
                </Flex>
            ) : null}

            {sddShippingTotal ? (
                <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                    {isNextDayDelivery ? (
                        <Text
                            data-testid="order-summary-next-day-shipping-label"
                            textStyle={textStyle}
                        >
                            <FormattedMessage
                                id="order_summary.next.day.shipping"
                                defaultMessage="Next-Day Delivery fee"
                            />
                        </Text>
                    ) : (
                        <Text
                            data-testid="order-summary-same-day-shipping-label"
                            textStyle={textStyle}
                        >
                            <FormattedMessage
                                id="order_summary.same.day.shipping"
                                defaultMessage="Same-Day Delivery fee"
                            />
                        </Text>
                    )}

                    <Text data-testid="order-summary-same-day-shipping-value" textStyle={textStyle}>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={sddShippingTotal}
                        />
                    </Text>
                </Flex>
            ) : null}

            {c_handlingFee > 0 ? (
                <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                    <Text data-testid="order-summary-handlingkey" textStyle={textStyle}>
                        <FormattedMessage
                            id="order_summary.handlingFee"
                            defaultMessage="Handling Fee"
                        />
                    </Text>

                    <Text data-testid="order-summary-handlingvalue" textStyle={textStyle}>
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={c_handlingFee}
                        />
                    </Text>
                </Flex>
            ) : null}

            <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                <Text data-testid="order-summary-sales-tax-label" textStyle={textStyle}>
                    <FormattedMessage id="order_summary.sales_tax" defaultMessage="Sales Tax" />
                </Text>

                <Text data-testid="order-summary-sales-tax-value" textStyle={textStyle}>
                    {taxTotal > 0 ? (
                        <FormattedNumber style="currency" currency={currency} value={taxTotal} />
                    ) : (
                        '-'
                    )}
                </Text>
            </Flex>

            <Divider marginTop={2} marginBottom={4} />

            <BasketOrderTotalSummary showSavings={isFromCart} />

            {giftCards.hasGiftCardsApplied && !isFromCart ? (
                <>
                    <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                        <Flex alignItems="center">
                            <Text
                                data-testid="order-summary-gift-cards-applied-label"
                                color="slt-success-green.1000"
                            >
                                <FormattedMessage
                                    id="order_summary.gift_cards_applied"
                                    defaultMessage="Gift cards applied"
                                />
                            </Text>
                            <Flex
                                height="17px"
                                width={6}
                                marginLeft={4}
                                background="slt-success-green.1000"
                                borderRadius={100}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Text
                                    data-testid="order-summary-gift-cards-applied-quantity"
                                    color="white"
                                    textAlign="center"
                                    fontSize="0.875rem"
                                    fontWeight={700}
                                >
                                    {giftCards.quantityApplied}
                                </Text>
                            </Flex>
                        </Flex>

                        <Text
                            data-testid="order-summary-gift-cards-applied-value"
                            color="slt-success-green.1000"
                        >
                            <FormattedNumber
                                style="currency"
                                currency={currency}
                                value={-giftCards.totalValueApplied}
                            />
                        </Text>
                    </Flex>

                    <Flex justifyContent="space-between" alignItems="center" marginTop={2}>
                        <Text data-testid="order-summary-remaining-payment-label">
                            <FormattedMessage
                                id="order_summary.remaining_payment"
                                defaultMessage="Remaining payment"
                            />
                        </Text>

                        <Text data-testid="order-summary-remaining-payment-value">
                            <FormattedNumber
                                style="currency"
                                currency={currency}
                                value={giftCards.remainingPayment}
                            />
                        </Text>
                    </Flex>
                </>
            ) : null}
        </>
    );
};

BasketSummary.defaultProps = {
    isFromCart: false,
};
